import React, { useMemo } from "react";
import { currentTierIIReportingYear } from "encamp-shared/src/constants/tierii";
import { useRegisterCommands, type Command } from "./CommandPaletteRegistry";
import { forceStartReplayAndGetLink } from "../sentry";
import {
  templateLinearTicket,
  buildLinearNewIssueUrl,
} from "./commandPalette/linear";

type DerivedContext = {
  tenantId?: string;
  encodedTenantId?: string;
  facilityId?: string;
  facilityDisplay?: string;
  encodedFacilityId?: string;
  reportId?: string;
  encodedReportId?: string;
};

export function DefaultCommandRegistrations({
  derived,
  tenantLabel,
  navigate,
  getAppBaseUrl,
  copyToClipboard,
  copyRichLink,
}: {
  derived: DerivedContext;
  tenantLabel: string;
  navigate: (path: string) => void;
  getAppBaseUrl: () => string;
  copyToClipboard: (t: string) => Promise<void> | void;
  copyRichLink: (u: string, l: string) => Promise<void> | void;
}): React.ReactElement | null {
  // Staff fulfillment navigation commands (always available)
  const staffNavCmds = useMemo<Command[]>(
    () => [
      {
        id: "nav-fulfillment-facilities",
        label: "Go to Chemical Facilities",
        description: "/staff/fulfillment/facilities",
        run: () => navigate("/staff/fulfillment/facilities"),
        signature: "nav:fulfillment-facilities",
      },
      {
        id: "nav-fulfillment-workflows",
        label: "Go to Workflows",
        description: "/staff/fulfillment/workflows",
        run: () => navigate("/staff/fulfillment/workflows"),
        signature: "nav:fulfillment-workflows",
      },
      {
        id: "nav-fulfillment-error-triage",
        label: "Go to Error Triage",
        description: "/staff/fulfillment/error-clustering",
        run: () => navigate("/staff/fulfillment/error-clustering"),
        signature: "nav:fulfillment-error-triage",
      },
      {
        id: "nav-fulfillment-waste",
        label: "Go to Waste Submissions",
        description: "/staff/fulfillment/waste",
        run: () => navigate("/staff/fulfillment/waste"),
        signature: "nav:fulfillment-waste",
      },
    ],
    [navigate]
  );

  useRegisterCommands(staffNavCmds, [staffNavCmds], "staff-nav");

  // Tenant default commands
  const tenantCmds = useMemo<Command[]>(() => {
    const items: Command[] = [];
    if (derived.tenantId && derived.encodedTenantId) {
      const tenantPath = `/o/${derived.encodedTenantId}`;
      const staffImplementationPath = `/staff/implementation/${derived.encodedTenantId}`;
      items.push(
        {
          id: "copy-tenant-link",
          label: `Copy tenant ${tenantLabel} as link`,
          description: tenantPath,
          run: () =>
            copyRichLink(`${getAppBaseUrl()}${tenantPath}`, tenantLabel),
          signature: `tenant-link:${tenantPath}:${tenantLabel}`,
        },
        {
          id: "copy-tenant-url",
          label: "Copy tenant URL",
          description: tenantPath,
          run: () => copyToClipboard(`${getAppBaseUrl()}${tenantPath}`),
          signature: `tenant-url:${tenantPath}`,
        },
        {
          id: "copy-tenant-id",
          label: "Copy tenant ID",
          description: derived.tenantId,
          run: () => {
            const v = derived.tenantId;
            if (!v) return;
            copyToClipboard(v);
          },
          signature: `tenant-id:${derived.tenantId ?? ""}`,
        },
        {
          id: "open-staff-implementation",
          label: "Open staff implementation for tenant",
          description: staffImplementationPath,
          run: () => navigate(staffImplementationPath),
          signature: `staff-implementation:${staffImplementationPath}`,
        }
      );

      if (!derived.facilityId && !derived.reportId) {
        const reportsBase = `/o/${derived.encodedTenantId}/chemicals/reports`;
        const reportsOmnisearch = encodeURIComponent(
          `reportingYear:${currentTierIIReportingYear}`
        );
        items.push(
          {
            id: "open-tenant-reports-current-year",
            label: "Open current year chemical reports",
            description: `${reportsBase}?omnisearch=${reportsOmnisearch}`,
            run: () =>
              navigate(`${reportsBase}?omnisearch=${reportsOmnisearch}`),
            signature: `tenant-reports:${reportsBase}?${reportsOmnisearch}`,
          },
          {
            id: "open-waste-overview",
            label: "Open waste overview",
            description: `/o/${derived.encodedTenantId}/waste/overview`,
            run: () => navigate(`/o/${derived.encodedTenantId}/waste/overview`),
            signature: `tenant-waste-overview:/o/${derived.encodedTenantId}/waste/overview`,
          }
        );
      }
    }
    return items;
  }, [
    derived.tenantId,
    derived.encodedTenantId,
    derived.facilityId,
    derived.reportId,
    tenantLabel,
    navigate,
    getAppBaseUrl,
    copyToClipboard,
    copyRichLink,
  ]);

  useRegisterCommands(tenantCmds, [tenantCmds], "default-tenant");

  // Facility default commands
  const facilityCmds = useMemo<Command[]>(() => {
    const items: Command[] = [];
    if (
      derived.encodedTenantId &&
      derived.encodedFacilityId &&
      derived.facilityId
    ) {
      const facilityProfilePath = `/o/${derived.encodedTenantId}/facilities/${derived.encodedFacilityId}/profile`;
      const facilityDisplayLabel =
        derived.facilityDisplay ?? String(derived.facilityId);
      items.push(
        {
          id: "copy-facility-link",
          label: `Copy facility ${facilityDisplayLabel} as link`,
          description: facilityProfilePath,
          run: () =>
            copyRichLink(
              `${getAppBaseUrl()}${facilityProfilePath}`,
              facilityDisplayLabel
            ),
          signature: `facility-link:${facilityProfilePath}:${facilityDisplayLabel}`,
        },
        {
          id: "copy-facility-url",
          label: "Copy facility URL",
          description: facilityProfilePath,
          run: () =>
            copyToClipboard(`${getAppBaseUrl()}${facilityProfilePath}`),
          signature: `facility-url:${facilityProfilePath}`,
        },
        {
          id: "copy-facility-id",
          label: "Copy facility ID",
          description: derived.facilityId,
          run: () => {
            const v = derived.facilityId;
            if (!v) return;
            copyToClipboard(v);
          },
          signature: `facility-id:${derived.facilityId ?? ""}`,
        },
        {
          id: "open-facility-profile",
          label: "Open facility profile",
          description: facilityProfilePath,
          run: () => navigate(facilityProfilePath),
          signature: `facility-profile:${facilityProfilePath}`,
        }
      );

      items.push(
        {
          id: "open-facility-inventory",
          label: "Open facility chemical inventory",
          description: `/o/${derived.encodedTenantId}/chemicals/inventory/${derived.encodedFacilityId}`,
          run: () =>
            navigate(
              `/o/${derived.encodedTenantId}/chemicals/inventory/${derived.encodedFacilityId}`
            ),
          signature: `facility-inventory:/o/${derived.encodedTenantId}/chemicals/inventory/${derived.encodedFacilityId}`,
        },
        {
          id: "open-facility-waste",
          label: "Open facility waste",
          description: `/o/${derived.encodedTenantId}/waste/facilities/${derived.encodedFacilityId}`,
          run: () =>
            navigate(
              `/o/${derived.encodedTenantId}/waste/facilities/${derived.encodedFacilityId}`
            ),
          signature: `facility-waste:/o/${derived.encodedTenantId}/waste/facilities/${derived.encodedFacilityId}`,
        }
      );

      const customerReportsPath = `/o/${derived.encodedTenantId}/chemicals/reports`;
      const customerOmnisearch = encodeURIComponent(
        `facilityId:${derived.facilityId} reportingYear:${currentTierIIReportingYear}`
      );
      items.push({
        id: "open-customer-reports-facility",
        label: "Open current year chemical reports for facility",
        description: `${customerReportsPath}?omnisearch=${customerOmnisearch}`,
        run: () =>
          navigate(`${customerReportsPath}?omnisearch=${customerOmnisearch}`),
        signature: `facility-reports:${customerReportsPath}?${customerOmnisearch}`,
      });

      if (!derived.reportId) {
        const staffFacilityPath = `/staff/fulfillment/f/${derived.encodedFacilityId}/${currentTierIIReportingYear}`;
        items.push({
          id: "open-staff-fulfillment-facility",
          label: "Open current year chemical report staff fulfillment",
          description: staffFacilityPath,
          run: () => navigate(staffFacilityPath),
          signature: `staff-facility-fulfillment:${staffFacilityPath}`,
        });
      }
    }
    return items;
  }, [
    derived.encodedTenantId,
    derived.encodedFacilityId,
    derived.facilityId,
    derived.facilityDisplay,
    derived.reportId,
    navigate,
    getAppBaseUrl,
    copyToClipboard,
    copyRichLink,
  ]);

  useRegisterCommands(facilityCmds, [facilityCmds], "default-facility");

  // Report default commands
  const reportCmds = useMemo<Command[]>(() => {
    const items: Command[] = [];
    if (
      derived.encodedTenantId &&
      derived.encodedReportId &&
      derived.reportId
    ) {
      const reportStepperPath = `/o/${derived.encodedTenantId}/chemicals/reports/${derived.encodedReportId}`;
      items.push(
        {
          id: "open-report-stepper",
          label: "Open chemical report",
          description: reportStepperPath,
          run: () => navigate(reportStepperPath),
          signature: `report-stepper:${reportStepperPath}`,
        },
        {
          id: "copy-report-id",
          label: "Copy report ID",
          description: derived.reportId,
          run: () => {
            const v = derived.reportId;
            if (!v) return;
            copyToClipboard(v);
          },
          signature: `report-id:${derived.reportId ?? ""}`,
        }
      );
    }
    if (derived.encodedReportId) {
      const fulfillmentReportPath = `/staff/fulfillment/r/${derived.encodedReportId}`;
      items.push({
        id: "open-report-fulfillment",
        label: "Open chemical report staff fulfillment",
        description: fulfillmentReportPath,
        run: () => navigate(fulfillmentReportPath),
        signature: `report-fulfillment:${fulfillmentReportPath}`,
      });
    }
    return items;
  }, [
    derived.encodedTenantId,
    derived.encodedReportId,
    derived.reportId,
    navigate,
    copyToClipboard,
  ]);

  useRegisterCommands(reportCmds, [reportCmds], "default-report");

  // Linear issue creation command (prefilled via URL)
  const envDep = import.meta.env.VITE_ENV ?? "unknown";
  const hrefDep =
    typeof window !== "undefined" ? window.location.href : getAppBaseUrl();
  const linearIssueCmds = useMemo<Command[]>(() => {
    const items: Command[] = [];
    const envLabel = envDep;
    const origin = getAppBaseUrl();
    const currentHref = hrefDep;
    const buildFacilityProfileUrl = () => {
      if (!derived.encodedTenantId || !derived.encodedFacilityId)
        return undefined;
      return `/o/${derived.encodedTenantId}/facilities/${derived.encodedFacilityId}/profile`;
    };
    items.push({
      id: "linear-create-issue",
      label: "Create Linear issue",
      description: "Opens Linear with prefilled description and context",
      run: () => {
        // Force start a replay if one isn't already recording
        const sentryReplayUrl = forceStartReplayAndGetLink();

        const href = currentHref;
        const facilityProfile = buildFacilityProfileUrl();
        const facilityProfileFull =
          derived.facilityId && facilityProfile
            ? `${origin}${facilityProfile}`
            : undefined;
        const pageTitle =
          typeof document !== "undefined" && document.title
            ? document.title
            : undefined;
        const { title: titleBase, description } = templateLinearTicket({
          href,
          envLabel,
          tenantLabel,
          tenantId: derived.tenantId,
          facilityDisplay:
            derived.facilityDisplay ??
            (derived.facilityId ? String(derived.facilityId) : undefined),
          facilityProfileUrl: facilityProfileFull,
          reportId: derived.reportId,
          sentryReplayUrl,
          pageTitle,
        });

        const url = buildLinearNewIssueUrl({ title: titleBase, description });
        if (typeof window !== "undefined") {
          window.open(url, "_blank", "noopener");
        }
      },
      signature: [
        envLabel,
        currentHref,
        tenantLabel ?? "",
        derived.tenantId ?? "",
        derived.facilityDisplay ?? "",
        buildFacilityProfileUrl() ?? "",
        derived.reportId ?? "",
      ].join("|"),
    });
    return items;
  }, [
    derived.encodedFacilityId,
    derived.encodedTenantId,
    derived.facilityDisplay,
    derived.facilityId,
    derived.reportId,
    derived.tenantId,
    getAppBaseUrl,
    tenantLabel,
    // ensure updates on env and URL changes
    envDep,
    hrefDep,
  ]);

  useRegisterCommands(linearIssueCmds, [linearIssueCmds], "default-linear");

  return null;
}
